import type { Message, ToolEvent } from "./index/_shared.js";

export type GoalStatus =
  "active" | "completed" | "blocked" | "paused" | "limit-reached";
export interface GoalSnapshot {
  sourceMessageId: string;
  objective: string;
  status: GoalStatus;
  reason: string;
  turns: number;
  runId?: string;
  runTurns?: number;
  maxTurns?: number;
  idleTurns?: number;
}

function goalReceipt(event: ToolEvent): GoalSnapshot | null {
  if (event.name !== "goal_evaluation" || event.status !== "completed")
    return null;
  const value = event.result as Partial<GoalSnapshot> | undefined;
  if (
    !value ||
    typeof value.sourceMessageId !== "string" ||
    typeof value.objective !== "string" ||
    !["active", "completed", "blocked", "paused", "limit-reached"].includes(
      value.status ?? "",
    ) ||
    typeof value.reason !== "string" ||
    !Number.isInteger(value.turns) ||
    (value.turns ?? -1) < 0
  )
    return null;
  return value as GoalSnapshot;
}

/** Evaluator output is untrusted. An invalid verdict must never restart work. */
export function parseGoalVerdict(
  content: string,
): Pick<GoalSnapshot, "status" | "reason"> {
  try {
    const value = JSON.parse(
      content.replace(/^```(?:json)?\s*|\s*```$/g, "").trim(),
    );
    if (
      ["active", "completed", "blocked"].includes(value.status) &&
      typeof value.reason === "string" &&
      value.reason.trim()
    ) {
      return { status: value.status, reason: value.reason.trim() };
    }
  } catch {
    /* Pause on malformed output rather than guessing. */
  }
  return {
    status: "paused",
    reason:
      "The goal check did not return a valid verdict. Review progress before continuing.",
  };
}

/** Goals belong to the latest user request on the selected conversation branch. */
export function latestGoal(
  messages: Pick<
    Message,
    "id" | "role" | "content" | "generationParameters" | "toolEvents"
  >[],
  fallback?: GoalSnapshot | null,
): GoalSnapshot | null {
  let index = messages.length - 1;
  while (index >= 0 && messages[index].role !== "user") index--;
  const source = messages[index];
  if (source && !source.generationParameters?.goal) return null;
  // A page of assistant fragments may no longer include the originating user.
  if (!source && !fallback) {
    for (
      let messageIndex = messages.length - 1;
      messageIndex >= 0 && !fallback;
      messageIndex--
    ) {
      const events = messages[messageIndex].toolEvents ?? [];
      for (
        let eventIndex = events.length - 1;
        eventIndex >= 0 && !fallback;
        eventIndex--
      )
        fallback = goalReceipt(events[eventIndex]);
    }
  }
  if (!source && !fallback) return null;
  const initial: GoalSnapshot =
    fallback && (!source || fallback.sourceMessageId === source.id)
      ? { ...fallback }
      : source
        ? {
            sourceMessageId: source.id,
            objective: source.content,
            status: "active",
            reason: "",
            turns: 0,
          }
        : { ...fallback! };
  for (const message of messages.slice(index + 1)) {
    for (const event of message.toolEvents ?? []) {
      const result = goalReceipt(event);
      if (result?.sourceMessageId === initial.sourceMessageId) {
        Object.assign(initial, {
          status: result.status,
          reason: result.reason,
          turns: result.turns,
          runId: result.runId,
          runTurns: result.runTurns,
          maxTurns: result.maxTurns,
          idleTurns: result.idleTurns,
        });
      }
    }
  }
  return initial;
}

/** Terminal verdicts win over loop guards; hitting a limit never means success. */
export function boundGoalVerdict(
  verdict: Pick<GoalSnapshot, "status" | "reason">,
  idleTurns: number,
  turn: number,
  maxTurns: number,
): Pick<GoalSnapshot, "status" | "reason"> {
  if (verdict.status !== "active") return verdict;
  if (idleTurns >= 2)
    return {
      status: "paused",
      reason:
        "No work tools were used for two consecutive turns. Review progress before continuing.",
    };
  if (turn >= maxTurns)
    return {
      status: "limit-reached",
      reason: `Reached the limit of ${maxTurns} turns for this run. Review progress before continuing.`,
    };
  return verdict;
}

export function goalEvaluationEvent(goal: GoalSnapshot): ToolEvent {
  return {
    id: crypto.randomUUID(),
    name: "goal_evaluation",
    arguments: {},
    result: goal,
    status: "completed",
  };
}
